import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { reduzirImagem } from '@/lib/imagem';

interface Props {
  rifaId: number;
}

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 8 * 1024 * 1024;

export default function CapaRifaEditor({ rifaId }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [capaUrl, setCapaUrl] = useState<string | null>(null);
  const [premioImagem, setPremioImagem] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroTecnico, setErroTecnico] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data } = await supabase.rpc('capa_da_rifa', { p_rifa: rifaId });
        if (!ativo) return;
        const c = (data ?? null) as {
          capa_url?: string | null;
          premio_imagem_url?: string | null;
        } | null;
        setCapaUrl(c?.capa_url ?? null);
        setPremioImagem(c?.premio_imagem_url ?? null);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar a capa.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [rifaId]);

  async function copiarErro() {
    if (!erroTecnico) return;
    try {
      await navigator.clipboard.writeText(erroTecnico);
    } catch {
      /* ignora */
    }
  }

  async function aoEscolher(arquivo: File | null) {
    if (!arquivo) return;
    setErro(null);
    setErroTecnico(null);
    setRecado(null);
    if (!EXTENSOES_OK.includes(arquivo.type)) {
      setErro('Use uma imagem jpg, png ou webp.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      setErro('Esta imagem tem mais de 8 MB. Escolha uma menor.');
      return;
    }
    setEnviando(true);
    try {
      const webp = await reduzirImagem(arquivo, 1600, 0.85, 'image/webp');
      const caminho = `rifas/${rifaId}/capa-${Date.now()}.webp`;
      const { error: errUpload } = await supabase.storage
        .from('capas')
        .upload(caminho, webp, { contentType: 'image/webp' });
      if (errUpload) {
        setErroTecnico(mensagemAmigavel(errUpload, 'Erro ao enviar a capa.'));
        return;
      }
      const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
      const url = data.publicUrl;
      const { error } = await supabase.rpc('definir_capa_da_rifa', {
        p_rifa: rifaId,
        p_url: url,
      });
      if (error) throw error;
      setCapaUrl(url);
      setRecado('Capa salva. Ela já aparece no talão, nas ações entre amigos e na página de compra.');
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível salvar a capa.'));
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const imagemMostrada = capaUrl ?? premioImagem;

  return (
    <section className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <h2 className="text-[17px] font-bold text-foreground-950">Capa da ação entre amigos</h2>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={enviando || carregando}
          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-image-add-line" />
          </span>
          {capaUrl ? 'Trocar' : 'Enviar capa'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="sr-only"
          onChange={(e) => aoEscolher(e.target.files?.[0] ?? null)}
        />
      </div>

      <p className="mt-1 px-1 text-[13px] text-foreground-500">
        É a imagem de fundo da página de compra, do talão e das suas ações entre amigos. Sem capa própria, a
        foto do 1º prêmio é usada.
      </p>

      {carregando ? (
        <div className="mt-3 h-40 animate-pulse rounded-2xl bg-secondary-100" />
      ) : (
        <div className="mt-3 flex items-start gap-4">
          <div className="h-24 w-40 shrink-0 overflow-hidden rounded-xl border border-secondary-200 bg-secondary-50">
            {imagemMostrada ? (
              <img
                src={imagemMostrada}
                alt="Capa da ação entre amigos"
                className="h-full w-full object-cover object-center"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-secondary-300">
                <i className="ri-image-line text-3xl" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 text-[13px] leading-relaxed text-foreground-600">
            {capaUrl ? (
              <p>
                Capa própria definida. A página usa esta imagem como fundo.
              </p>
            ) : premioImagem ? (
              <p>
                Sem capa própria — a página está usando a foto do 1º prêmio. Envie uma capa para
                deixar a ação entre amigos com a cara certa.
              </p>
            ) : (
              <p>
                Sem capa e sem foto de prêmio — a página cai no degradê padrão. Envie uma capa.
              </p>
            )}
          </div>
        </div>
      )}

      {enviando && (
        <p className="mt-2 flex items-center gap-2 px-1 text-sm text-secondary-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </span>
          Enviando capa…
        </p>
      )}
      {recado && <p className="mt-2 px-1 text-sm text-accent-700">{recado}</p>}
      {erro && (
        <p className="mt-2 px-1 text-sm text-danger-600" role="alert">
          {erro}
        </p>
      )}
      {erroTecnico && (
        <div className="mt-3 rounded-xl border border-danger-200 bg-danger-50 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-danger-700">Não foi possível salvar a capa.</p>
              <p className="mt-1 break-words text-sm text-danger-700">{erroTecnico}</p>
            </div>
            <button
              type="button"
              onClick={copiarErro}
              className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg border border-danger-300 bg-white px-2 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-file-copy-line" />
              </span>
              Copiar erro
            </button>
          </div>
        </div>
      )}
    </section>
  );
}